import { LitElement, html, css, nothing, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

/**
 * Designed for Easy Pricing tooltip in mind to take in html strings
 */

@customElement('rq-comparetooltip')
export class CompareTooltip extends LitElement {
    static styles = css`
        :host {
            position: relative;
            display: flex;
            height: 7rem; /* default for not mobile */
            justify-content: center;
        }
        @media (max-width: 992px) {
            :host {
                height: 4rem; /* for tablet */
            }
        }

        @media (max-width: 768px) {
            :host {
                height: 3.25rem; /* for mobile */
            }
        }
        .lh-tooltip {
            display: inline-block;
        }
        .trigger {
            cursor: pointer;
            display: inline-block;
            position: relative;
            margin-right: 2px;

            max-width: fit-content;
        }
        .trigger::after {
            background-image: linear-gradient(
                90deg,
                var(--mode-tooltip-trigger-color, #00aeef),
                var(--mode-tooltip-trigger-color, #00aeef) 65%,
                transparent 65%,
                transparent 100%
            );
            background-size: 8px 1px;
            border: none;
            content: '';
            display: var(--rq-tooltip-line, block);
            height: 1px;
            position: absolute;
            width: 100%;
        }
        .tooltip-content {
            visibility: hidden;
            display: none;
            position: absolute;
            background-color: var(--rq-tooltip-background-color, var(--lh-color-neutral-900, #111313));
            color: var(--lh-color-neutral-white, #ffffff);
            font-family: var(--lh-font-family-sans-serif);
            font-size: var(--lh-tooltip-font-size, 0.75rem);
            left: var(--rq-tooltip-content-left, auto);
            right: var(--rq-tooltip-content-right, auto);
            top: var(--rq-tooltip-content-top, -50%);
            transform: translateY(-50%);
            line-height: var(--lh-line-height-1-333);
            width: fit-content;
            max-width: 300px;
            padding: var(--lh-tooltip-padding, 8px);
            z-index: 10;
            border: var(--rq-tooltip-border, none);
            border-radius: var(--lh-tooltip-border-radius, 0.25rem);
        }

        .tooltip-content::after {
            content: '';
            display: var(--rq-arrow-display, block);
            position: absolute;
            top: 50%;
            left: var(--rq-tooltip-arrow-left, auto);
            right: var(--rq-tooltip-arrow-right, auto);
            transform: var(--rq-tooltip-arrow-transform, translate(-100%, -50%));
            border-width: 5px;
            border-style: solid;
            border-color: var(
                --rq-tooltip-arrow-border-color,
                transparent var(--lh-color-neutral-900, #111313) transparent transparent
            );
        }
        .header,
        .description {
            white-space: pre-wrap;
            color: var(--lh-color-neutral-white, #ffffff);
            font-size: var(--lh-tooltip-font-size, 0.75rem);
        }
        .description {
            display: flex;
            flex-direction: column;
            gap: 4px;
            align-items: flex-start;
        }
        .show {
            visibility: visible;
            opacity: 1;
            transition: opacity 300ms;
            display: block;
        }
        p,
        ul,
        li {
            margin: 0;
            padding-left: 10px;
        }
        ul {
            padding-top: 4px;
        }
        .line-item {
            display: grid;
            grid-template-columns: 20px 50px auto;
            align-items: center;
            gap: 8px;
        }
    `;

    @property()
    header!: string | TemplateResult;

    @property()
    description!: string;

    @property()
    position = 'right';

    @property({ attribute: 'background-color' })
    backgroundColor = 'var(--lh-color-neutral-900, #111313)';

    @property({ attribute: 'border-color' })
    borderColor?: string;

    @property({ attribute: 'hide-line', type: Boolean })
    hideLine: boolean = false;

    @query('.description')
    descriptionElement!: HTMLDivElement;

    @query('.trigger')
    triggerElement!: HTMLDivElement;

    @state()
    open: boolean = false;

    connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener('mouseenter', () => {
            this.open = true;
        });
        this.addEventListener('mouseleave', () => {
            this.open = false;
        });
        this.addEventListener('focusin', () => {
            this.open = true;
        });
        this.addEventListener('focusout', () => {
            this.open = false;
        });
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener('mouseenter', () => {
            this.open = true;
        });
        this.removeEventListener('mouseleave', () => {
            this.open = false;
        });
        this.removeEventListener('focusin', () => {
            this.open = true;
        });
        this.removeEventListener('focusout', () => {
            this.open = false;
        });
    }

    firstUpdated(): void {
        this.updateDescription();
    }

    updated(changedProperties: Map<string, unknown>) {
        if (changedProperties.has('description')) {
            this.updateDescription();
        }
    }

    private updateDescription() {
        this.descriptionElement.innerHTML = this.description;
        this.style.setProperty('--rq-tooltip-line', this.hideLine ? 'none' : 'block');
        const arrowColor = this.borderColor || 'var(--lh-color-neutral-900, #111313)';
        switch (this.position) {
            case 'top':
                this.style.setProperty('--rq-arrow-display', 'none');
                this.style.setProperty('--rq-tooltip-content-top', '-66%');
                this.style.setProperty('--rq-tooltip-content-left', '0');
                this.style.setProperty(
                    '--rq-tooltip-arrow-border-color',
                    `transparent  transparent transparent transparent`
                );
                break;
            case 'left':
                this.style.setProperty('--rq-tooltip-content-left', 'auto');
                this.style.setProperty('--rq-tooltip-arrow-left', 'auto');
                this.style.setProperty('--rq-tooltip-content-right', 'calc(100% + 4px)');
                this.style.setProperty('--rq-tooltip-arrow-right', '0');
                this.style.setProperty('--rq-tooltip-arrow-transform', 'translate(100%, 50%)');
                this.style.setProperty('--rq-tooltip-content-top', '50%');
                this.style.setProperty(
                    '--rq-tooltip-arrow-border-color',
                    `transparent  transparent transparent ${arrowColor}`
                );
                break;
            case 'right':
                this.style.setProperty('--rq-tooltip-content-left', 'calc(100% + 4px)');
                this.style.setProperty('--rq-tooltip-arrow-left', '0');
                this.style.setProperty('--rq-tooltip-content-right', 'auto');
                this.style.setProperty('--rq-tooltip-arrow-right', 'auto');
                this.style.setProperty('--rq-tooltip-arrow-transform', 'translate(-100%, -50%)');
                this.style.setProperty('--rq-tooltip-content-top', '50%');
                this.style.setProperty(
                    '--rq-tooltip-arrow-border-color',
                    `transparent ${arrowColor} transparent transparent`
                );
        }

        this.style.setProperty(
            '--rq-tooltip-background-color',
            this.backgroundColor ? this.backgroundColor : 'var(--lh-color-neutral-900, #111313);'
        );
        this.style.setProperty('--rq-tooltip-border', this.borderColor ? `1px solid ${this.borderColor}` : 'none');
        this.style.setProperty(
            '--rq-tooltip-arrow-border-color',
            this.position === 'right'
                ? `transparent ${arrowColor} transparent transparent`
                : `transparent  transparent transparent ${arrowColor}`
        );
    }

    render() {
        return html`
            <div class="lh-tooltip">
                <div class="trigger" tabindex="0" aria-describedby="tooltip"><slot></slot></div>
                <div class=${classMap({ 'tooltip-content': true, show: this.open })} role="tooltip" id="tooltip">
                    ${this.header
                        ? html`<lh-typography
                              ><span class="header"><strong>${this.header}</strong></span></lh-typography
                          >`
                        : nothing}<lh-typography><div class="description"></div></lh-typography>
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-comparetooltip': CompareTooltip;
    }
}
